import { useState } from 'react';
import { IconDotsVertical, IconLogout, IconUserCircle } from '@tabler/icons-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { logout, useUserStore } from '@/stores/userStore';
import { UserInfoDialog } from './UserInfoDialog';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { TruncatedText } from '@/components/TruncatedText';

export function NavUser() {
  const { isMobile } = useSidebar();
  const { userInfo } = useUserStore();
  const [openUserInfoDialog, setOpenUserInfoDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();

  /** 登出请求完成后再离开当前页面，避免清空 token 前丢失请求鉴权 */
  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    const toastId = toast.loading('正在退出登录...');
    await logout();
    navigate('/login', { replace: true });
    toast.success('已退出登录', { id: toastId });
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground gap-2.5 px-5"
            >
              <Avatar className="h-7 w-7 rounded-sm">
                <AvatarImage
                  src={userInfo?.avatarUrl}
                  alt={userInfo?.nickname || userInfo?.username || userInfo?.email}
                />
                <AvatarFallback className="bg-blue-600 rounded-sm text-white text-base">
                  {userInfo?.email?.slice(0, 1)?.toLocaleUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight mr-4.5 group-data-[collapsible=compact]:hidden">
                <TruncatedText className="font-medium">
                  {userInfo?.nickname || userInfo?.username || userInfo?.email}
                </TruncatedText>
                <TruncatedText className="text-muted-foreground text-xs">
                  {userInfo?.email}
                </TruncatedText>
              </div>
              <IconDotsVertical className="ml-auto size-4 group-data-[collapsible=compact]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-5 py-4 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage
                    src={userInfo?.avatarUrl}
                    alt={userInfo?.nickname || userInfo?.username || userInfo?.email}
                  />
                  <AvatarFallback className="rounded-lg">
                    {userInfo?.email?.slice(0, 2)?.toLocaleUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <TruncatedText className="font-medium">
                    {userInfo?.nickname || userInfo?.username || userInfo?.email}
                  </TruncatedText>
                  <TruncatedText className="text-muted-foreground text-xs">
                    {userInfo?.email}
                  </TruncatedText>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => setOpenUserInfoDialog(true)}
              >
                <IconUserCircle />
                个人信息
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              <IconLogout />
              退出账号
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      <UserInfoDialog open={openUserInfoDialog} onOpenChange={setOpenUserInfoDialog} />
    </SidebarMenu>
  );
}
